import React, { useState, useEffect } from 'react';
import { ArrowLeft, CheckCircle, XCircle, Clock, MessageSquare, User, Calendar, DollarSign } from 'lucide-react';
import Swal from 'sweetalert2';

export default function RechargeRequestsPage({ onBack }) {
  const [requests, setRequests] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // 'all', 'pending', 'approved', 'rejected'

  useEffect(() => {
    fetchRequests();
  }, []);

  const fetchRequests = async () => {
    try {
      setIsLoading(true);
      const token = localStorage.getItem('authToken');
      
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/recharge-requests`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) throw new Error('Erreur lors du chargement');

      const data = await response.json();
      setRequests(data.requests);
    } catch (error) {
      Swal.fire({
        icon: 'error',
        title: 'Erreur',
        text: 'Impossible de charger les demandes',
        confirmButtonColor: '#FF7B47'
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleApprove = async (request) => {
    const { value: notes } = await Swal.fire({
      title: 'Approuver la demande',
      html: `
        <p class="text-gray-700 mb-4">
          Approuver le rechargement de <strong>${parseFloat(request.amount).toFixed(3)} TND</strong> 
          pour <strong>${request.libelle_partenaire}</strong> ?
        </p>
      `,
      input: 'textarea',
      inputLabel: 'Notes administrateur (optionnel)',
      inputPlaceholder: 'Ajouter des notes...',
      showCancelButton: true,
      confirmButtonText: 'Approuver',
      cancelButtonText: 'Annuler',
      confirmButtonColor: '#10b981',
      cancelButtonColor: '#6b7280'
    });

    if (notes === undefined) return; // User clicked cancel

    try {
      const token = localStorage.getItem('authToken');
      
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/recharge-requests/${request.id_request}/approve`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ adminNotes: notes })
      });

      if (!response.ok) throw new Error('Erreur lors de l\'approbation');

      const data = await response.json();

      Swal.fire({
        icon: 'success',
        title: 'Approuvé',
        html: `
          <p>Demande approuvée avec succès!</p>
          <p class="text-sm text-gray-600 mt-2">
            Nouveau solde: <strong>${data.newBalance.toFixed(3)} TND</strong>
          </p>
        `,
        confirmButtonColor: '#FF7B47'
      });

      fetchRequests();

    } catch (error) {
      Swal.fire({
        icon: 'error',
        title: 'Erreur',
        text: error.message,
        confirmButtonColor: '#FF7B47'
      });
    }
  };

  const handleReject = async (request) => {
    const { value: notes } = await Swal.fire({
      title: 'Rejeter la demande',
      html: `
        <p class="text-gray-700 mb-4">
          Rejeter le rechargement de <strong>${parseFloat(request.amount).toFixed(3)} TND</strong> 
          pour <strong>${request.libelle_partenaire}</strong> ?
        </p>
      `,
      input: 'textarea',
      inputLabel: 'Raison du rejet (optionnel)',
      inputPlaceholder: 'Expliquer pourquoi...',
      showCancelButton: true,
      confirmButtonText: 'Rejeter',
      cancelButtonText: 'Annuler',
      confirmButtonColor: '#ef4444',
      cancelButtonColor: '#6b7280'
    });

    if (notes === undefined) return;

    try {
      const token = localStorage.getItem('authToken');
      
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/recharge-requests/${request.id_request}/reject`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ adminNotes: notes })
      });

      if (!response.ok) throw new Error('Erreur lors du rejet');

      Swal.fire({
        icon: 'success',
        title: 'Rejeté',
        text: 'Demande rejetée avec succès',
        confirmButtonColor: '#FF7B47'
      });

      fetchRequests();

    } catch (error) {
      Swal.fire({
        icon: 'error',
        title: 'Erreur',
        text: error.message,
        confirmButtonColor: '#FF7B47'
      });
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'pending':
        return <span className="px-3 py-1 bg-yellow-100 text-yellow-700 rounded-full text-sm font-semibold flex items-center gap-1">
          <Clock size={14} /> En attente
        </span>;
      case 'approved':
        return <span className="px-3 py-1 bg-green-100 text-green-700 rounded-full text-sm font-semibold flex items-center gap-1">
          <CheckCircle size={14} /> Approuvé
        </span>;
      case 'rejected':
        return <span className="px-3 py-1 bg-red-100 text-red-700 rounded-full text-sm font-semibold flex items-center gap-1">
          <XCircle size={14} /> Rejeté
        </span>;
      default:
        return null;
    }
  };

  const filteredRequests = requests.filter(r => 
    filter === 'all' || r.status === filter
  );

  const stats = {
    pending: requests.filter(r => r.status === 'pending').length,
    approved: requests.filter(r => r.status === 'approved').length,
    rejected: requests.filter(r => r.status === 'rejected').length
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="container mx-auto px-4 py-6">
        
        {/* Header */}
        <div className="flex items-center gap-4 mb-6">
          <button
            onClick={onBack}
            className="flex items-center gap-2 text-[#2B4979] hover:text-[#FF7B47] font-semibold transition"
          >
            <ArrowLeft size={20} />
            Retour
          </button>
          <h1 className="text-3xl font-bold text-[#2B4979]">Demandes de Rechargement</h1>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <div className="bg-yellow-50 border-2 border-yellow-200 rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-yellow-700 text-sm font-semibold">En attente</p>
                <p className="text-3xl font-bold text-yellow-800">{stats.pending}</p>
              </div>
              <Clock size={40} className="text-yellow-400" />
            </div>
          </div>

          <div className="bg-green-50 border-2 border-green-200 rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-green-700 text-sm font-semibold">Approuvées</p>
                <p className="text-3xl font-bold text-green-800">{stats.approved}</p>
              </div>
              <CheckCircle size={40} className="text-green-400" />
            </div>
          </div>

          <div className="bg-red-50 border-2 border-red-200 rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-red-700 text-sm font-semibold">Rejetées</p>
                <p className="text-3xl font-bold text-red-800">{stats.rejected}</p>
              </div>
              <XCircle size={40} className="text-red-400" />
            </div>
          </div>
        </div>

        {/* Filter Tabs */}
        <div className="bg-white rounded-lg shadow-md p-4 mb-6">
          <div className="flex gap-2">
            <button
              onClick={() => setFilter('all')}
              className={`px-4 py-2 rounded-lg font-semibold transition ${
                filter === 'all' 
                  ? 'bg-[#2B4979] text-white' 
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Toutes ({requests.length})
            </button>
            <button
              onClick={() => setFilter('pending')}
              className={`px-4 py-2 rounded-lg font-semibold transition ${
                filter === 'pending' 
                  ? 'bg-yellow-500 text-white' 
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              En attente ({stats.pending})
            </button>
            <button
              onClick={() => setFilter('approved')}
              className={`px-4 py-2 rounded-lg font-semibold transition ${
                filter === 'approved' 
                  ? 'bg-green-500 text-white' 
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Approuvées ({stats.approved})
            </button>
            <button
              onClick={() => setFilter('rejected')}
              className={`px-4 py-2 rounded-lg font-semibold transition ${
                filter === 'rejected' 
                  ? 'bg-red-500 text-white' 
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Rejetées ({stats.rejected})
            </button>
          </div>
        </div>

        {/* Requests List */}
        {isLoading ? (
          <div className="text-center py-12">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-[#FF7B47]"></div>
            <p className="mt-4 text-gray-600">Chargement...</p>
          </div>
        ) : filteredRequests.length === 0 ? (
          <div className="bg-white rounded-lg shadow-md p-12 text-center">
            <p className="text-gray-500 text-lg">Aucune demande trouvée</p>
          </div>
        ) : (
          <div className="space-y-4">
            {filteredRequests.map((request) => (
              <div key={request.id_request} className="bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition">
                
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-[#2B4979] rounded-full flex items-center justify-center text-white font-bold text-xl">
                      {request.prenom[0]}{request.nom[0]}
                    </div>
                    
                    <div>
                      <h3 className="text-xl font-bold text-[#2B4979]">{request.libelle_partenaire}</h3>
                      <div className="flex items-center gap-2 text-sm text-gray-600 mt-1">
                        <User size={14} />
                        <span>{request.prenom} {request.nom}</span>
                        <span className="text-gray-400">•</span>
                        <span>{request.email}</span>
                      </div>
                    </div>
                  </div>

                  <div className="text-right">
                    {getStatusBadge(request.status)}
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4 p-4 bg-gray-50 rounded-lg">
                  <div>
                    <p className="text-xs text-gray-500 mb-1">Montant demandé</p>
                    <p className="text-2xl font-bold text-[#FF7B47] flex items-center gap-1">
                      {/* <DollarSign size={20} /> */}
                      {parseFloat(request.amount).toFixed(3)} TND
                    </p>
                  </div>

                  <div>
                    <p className="text-xs text-gray-500 mb-1">Date de demande</p>
                    <p className="text-sm font-semibold text-gray-700 flex items-center gap-1">
                      <Calendar size={16} />
                      {new Date(request.requested_at).toLocaleString('fr-FR')}
                    </p>
                  </div>

                  {request.payment_method && (
                    <div>
                      <p className="text-xs text-gray-500 mb-1">Méthode de paiement</p>
                      <p className="text-sm font-semibold text-gray-700">{request.payment_method}</p>
                    </div>
                  )}
                </div>

                {request.admin_notes && (
                  <div className="mb-4 p-3 bg-blue-50 border-l-4 border-blue-400 rounded">
                    <p className="text-xs text-blue-700 font-semibold mb-1 flex items-center gap-1">
                      <MessageSquare size={14} /> Notes administrateur
                    </p>
                    <p className="text-sm text-gray-700">{request.admin_notes}</p>
                  </div>
                )}

                {request.status === 'pending' && (
                  <div className="flex items-center gap-3 pt-4 border-t border-gray-200">
                    <button
                      onClick={() => handleApprove(request)}
                      className="flex-1 flex items-center justify-center gap-2 bg-green-500 hover:bg-green-600 text-white px-6 py-3 rounded-lg font-semibold transition"
                    >
                      <CheckCircle size={20} />
                      Approuver
                    </button>
                    <button
                      onClick={() => handleReject(request)}
                      className="flex-1 flex items-center justify-center gap-2 bg-red-500 hover:bg-red-600 text-white px-6 py-3 rounded-lg font-semibold transition"
                    >
                      <XCircle size={20} />
                      Rejeter
                    </button>
                  </div>
                )}

                {request.status !== 'pending' && (
                  <div className="pt-4 border-t border-gray-200 text-sm text-gray-600">
                    <p>
                      {request.status === 'approved' ? 'Approuvé' : 'Rejeté'} le{' '}
                      {new Date(request.processed_at).toLocaleString('fr-FR')}
                    </p>
                  </div>
                )}

              </div>
            ))}
          </div>
        )}

      </div>
    </div>
  );
}
